import type { FormEvent } from 'react'
import { useOptionalT } from '../i18n/LocaleProvider'

export interface SearchModalProps {
  open: boolean
  onClose: () => void
  placeholder?: string
  onSubmit?: (query: string) => void
}

/**
 * The search overlay, byte-identical across all 19 source pages.
 * `.ul-search-form-wrapper.active` is what reveals it.
 *
 * The original form has `action="#"` and no handler, so submitting reloads the
 * page. Here submission is prevented by default and forwarded to `onSubmit` if a
 * caller wants it — same rendering, no accidental navigation.
 */
export function SearchModal({ open, onClose, placeholder: placeholderProp, onSubmit }: SearchModalProps) {
  const t = useOptionalT()
  const placeholder = placeholderProp ?? t('ul.searchModal.placeholder')
  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    const value = new FormData(e.currentTarget).get('search')
    onSubmit?.(typeof value === 'string' ? value : '')
  }

  return (
    <div className={`ul-search-form-wrapper flex-grow-1 flex-shrink-0${open ? ' active' : ''}`}>
      <button className="ul-search-closer" onClick={onClose} aria-label="Close search">
        <i className="flaticon-close" />
      </button>

      <form action="#" className="ul-search-form" onSubmit={handleSubmit}>
        <div className="ul-search-form-right">
          <input type="search" name="search" id="ul-search" placeholder={placeholder} />
          <button type="submit" aria-label="Search">
            <span className="icon">
              <i className="flaticon-search" />
            </span>
          </button>
        </div>
      </form>
    </div>
  )
}
